import React, { useCallback, useMemo, useState } from 'react';
import {
  Sparkles,
  RefreshCw,
  AlertTriangle,
  Phone,
  Loader2
} from 'lucide-react';
import { useUserFavoritesContext } from '../contexts/UserFavoritesContext';
import { useTrackedLinkModal } from '../hooks';
import { ContentModal } from '../components/ui';
import { useVitalityRecommendations } from '../hooks/useVitalityRecommendations';
import { VitalityResourceCard } from '../components/vitality/VitalityResourceCard';
import { VitalityGroup, VitalityItem } from '../types/vitality';

// Pillar visual accents (kept lightweight to avoid pulling new design tokens).
const PILLAR_ACCENT: Record<string, { ring: string; chip: string }> = {
  Sleep: { ring: 'ring-indigo-300', chip: 'bg-indigo-50 text-indigo-700' },
  Breathe: { ring: 'ring-sky-300', chip: 'bg-sky-50 text-sky-700' },
  Move: { ring: 'ring-emerald-300', chip: 'bg-emerald-50 text-emerald-700' },
  Reflect: { ring: 'ring-violet-300', chip: 'bg-violet-50 text-violet-700' },
  Eat: { ring: 'ring-amber-300', chip: 'bg-amber-50 text-amber-700' },
  Learn: { ring: 'ring-teal-300', chip: 'bg-teal-50 text-teal-700' },
  Crisis: { ring: 'ring-red-400', chip: 'bg-red-50 text-red-700' }
};

const VitalityPage: React.FC = () => {
  const {
    data,
    partialGroups,
    loading,
    refreshing,
    error,
    progress,
    stale,
    fromCache,
    refresh
  } = useVitalityRecommendations();

  const { favorites, toggleFavorite } = useUserFavoritesContext();
  const { isOpen, url, title, allowCredentials, trustDomain, openLink, closeModal } =
    useTrackedLinkModal();

  const [togglingUrl, setTogglingUrl] = useState<string | null>(null);

  // Build a set of favorited URLs for quick lookup
  const favoriteUrlSet = useMemo(() => {
    const s = new Set<string>();
    for (const f of favorites) {
      const u = f.payload?.url || f.payload?.metadata?.link;
      if (u) s.add(u);
    }
    return s;
  }, [favorites]);

  const handleToggleFavorite = useCallback(
    async (item: VitalityItem) => {
      setTogglingUrl(item.url);
      try {
        await toggleFavorite(item.kind, item.url, {
          itemId: item.url,
          title: item.title,
          description: item.description,
          url: item.url,
          thumbnail: item.thumbnailUrl || undefined,
          metadata: {
            source: 'vitality',
            kind: item.kind,
            language: item.language,
            link: item.url
          }
        });
      } catch (err) {
        console.warn('[Vitality] toggle favorite failed', err);
      } finally {
        setTogglingUrl(null);
      }
    },
    [toggleFavorite]
  );

  // Use streamed groups while loading; otherwise final data.groups.
  const groupsToRender: VitalityGroup[] = data?.groups?.length
    ? data.groups
    : partialGroups;

  const isInitialLoading = loading && groupsToRender.length === 0 && !data;

  return (
    <div className="h-full flex flex-col bg-rm-bg-0">
      {/* Header */}
      <div className="bg-rm-bg-0 border-b border-rm-neutral-600/20 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-rm-accent-300/20 rounded-full flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-rm-accent-600" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-rm-primary-700">Vitality</h1>
              <p className="text-sm text-rm-neutral-600">
                Personalized resources based on your wellbeing
              </p>
            </div>
          </div>
          <button
            onClick={() => refresh()}
            disabled={loading || refreshing}
            className="flex items-center gap-2 px-3 py-2 text-sm text-rm-neutral-600 hover:text-rm-primary-700 hover:bg-rm-bg-50 rounded-card transition-colors disabled:opacity-50"
            title="Refresh recommendations"
          >
            <RefreshCw
              className={`w-4 h-4 ${loading || refreshing ? 'animate-spin' : ''}`}
            />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>

        {data?.intro && !isInitialLoading && (
          <p className="text-sm text-rm-ink-700 mt-2">{data.intro}</p>
        )}

        {stale && fromCache && (refreshing || loading) && (
          <div className="mt-3 flex items-center gap-2 text-xs text-rm-neutral-600 bg-rm-bg-50 border border-rm-neutral-600/20 rounded-card px-3 py-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            <span>Refreshing your recommendations…</span>
          </div>
        )}

        {progress && (loading || refreshing) && (
          <div className="mt-2 flex items-center gap-2 text-xs text-rm-neutral-600">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span>{progress.label}</span>
          </div>
        )}

        {error && (
          <div className="mt-3 flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-card px-3 py-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium">Couldn’t load recommendations</div>
              <div className="text-xs">{error}</div>
            </div>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6">
        {data?.distress && (
          <div className="mb-6 p-4 rounded-card bg-red-50 border border-red-200 flex items-start gap-3">
            <Phone className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">
              <div className="font-semibold mb-1">If you need immediate support</div>
              <div>
                You don’t have to face this alone. Please review the support
                resources below or reach out to someone you trust.
              </div>
            </div>
          </div>
        )}

        {data?.coldStart && !isInitialLoading && (
          <div className="mb-6 p-4 rounded-card bg-rm-bg-50 border border-rm-neutral-600/20 text-sm text-rm-ink-700">
            We don’t have much to go on yet. Try a chat or update your wellbeing
            check-in to get more tailored suggestions.
          </div>
        )}

        {isInitialLoading && (
          <div className="space-y-6">
            {[0, 1, 2].map(i => (
              <div key={i} className="space-y-3">
                <div className="h-5 w-48 bg-rm-bg-50 rounded animate-pulse" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[0, 1, 2, 3].map(j => (
                    <div
                      key={j}
                      className="h-[220px] rounded-card bg-rm-bg-50 animate-pulse"
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {!isInitialLoading && groupsToRender.length === 0 && !error && (
          <div className="text-sm text-rm-neutral-600">
            No recommendations available right now.
          </div>
        )}

        <div className="space-y-8">
          {groupsToRender.map(group => {
            const accent = PILLAR_ACCENT[group.pillar] || {
              ring: 'ring-rm-neutral-300',
              chip: 'bg-rm-bg-50 text-rm-ink-700'
            };
            return (
              <section key={group.pillar}>
                <div className="flex items-center gap-3 mb-2">
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ${accent.chip} ${accent.ring}`}
                  >
                    {group.label || group.pillar}
                  </span>
                  {group.drivers?.length > 0 && (
                    <span className="text-xs text-rm-neutral-600">
                      Suggested because of{' '}
                      {group.drivers
                        .map(d => d.key.replace(/^wellbeing_/, '').replace(/_/g, ' '))
                        .slice(0, 2)
                        .join(', ')}
                    </span>
                  )}
                </div>
                {group.reason && (
                  <p className="text-sm text-rm-ink-700 mb-3">{group.reason}</p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {group.items.map(item => (
                    <VitalityResourceCard
                      key={item.url}
                      item={item}
                      isFavorited={favoriteUrlSet.has(item.url)}
                      toggling={togglingUrl === item.url}
                      onOpen={openLink}
                      onToggleFavorite={handleToggleFavorite}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </div>

      <ContentModal
        url={url}
        title={title}
        isOpen={isOpen}
        onClose={closeModal}
        allowCredentials={allowCredentials}
        trustDomain={trustDomain}
      />
    </div>
  );
};

export default VitalityPage;
